import ChevronRight from "@mui/icons-material/ChevronRight";
import EditOutlined from "@mui/icons-material/EditOutlined";
import KeyboardReturnOutlined from "@mui/icons-material/KeyboardReturnOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import SearchOffOutlined from "@mui/icons-material/SearchOffOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import { ModuleRegistry } from "ag-grid-community";
import { RichSelectModule } from "ag-grid-enterprise";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";

import { loanDrilldownSet } from "../../redux/actions/loanDrilldownActions";

import Flx from "../../components/layout/Flx";
import ScreenHeader from "../../components/layout/ScreenHeader";
import Htag from "../../components/typography/Htag";

import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import size from "lodash/size";
import toString from "lodash/toString";
import RffLoanDataGroup from "../../components/finalForm/shared/RffLoanDataGroup";

import {
  AddAlertOutlined,
  ErrorOutline,
  LocalOfferOutlined,
  PushPin,
  PushPinOutlined,
  SearchOutlined,
  DescriptionOutlined,
  InfoOutlined,
  FolderOutlined,
  BusinessOutlined,
  PeopleOutlined,
  HomeWorkOutlined,
  RuleOutlined,
  ReportProblemOutlined,
  ContactsOutlined,
  CalculateOutlined,
  GavelOutlined,
  MailOutlined,
  PaidOutlined,
  ChecklistOutlined,
  StarRounded,
  StarOutlineRounded,
} from "@mui/icons-material";
import { red } from "@mui/material/colors";
import LoanChecklist from "../../components/common/Loan/LoanChecklist/LoanChecklist";
import LoanPermittedUsersAvatarStack from "../../components/common/Loan/LoanPermittedUsersAvatarStack";
import LoanTagsModal from "../../components/common/Loan/LoanTags/LoanTagsModal";
import LoanTagsRenderer from "../../components/common/Loan/LoanTags/LoanTagsRenderer";
import UpdateLoanDataModal from "../../components/common/Loan/UpdateLoanDataModal";
import RffDisplayField from "../../components/finalForm/inputs/RffDisplayField";
import ScreenContent from "../../components/layout/ScreenContent";
import LoanSearchModal from "../../components/modals/common/LoanSearchModal";
import FilePreviewSideCardModal from "../../components/modals/FilePreviewSideCardModal";
import Txt from "../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useEnv } from "../../hooks/useEnv";
import { useMediaQueryHook } from "../../hooks/useMediaQueryHook";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { useReduxLoanDrilldownHook } from "../../hooks/useReduxLoanDrilldownHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import {
  loanPipelineClear,
  loanPipelineSet,
} from "../../redux/actions/loanPipelineActions";
import { navSidebarClose } from "../../redux/actions/navSidebarActions";
import { sidebarClear } from "../../redux/actions/sidebarActions";
import {
  workspaceTabsAdd,
  workspaceTabsUnpin,
} from "../../redux/reducers/workspaceTabsReducer";
import AddToLoanDropdownButton from "./buttons/AddToLoanDropdownButton";
import LoanSyncDropdownButton from "./buttons/LoanSyncDropdownButton";
import SendLoanApplicationsDropdownButton from "./buttons/SendLoanApplicationsDropdownButton";
import UploadToLoanDropdownButton from "./buttons/UploadToLoanDropdownButton";
import LoanDrilldownRouterTabSwitcher from "./components/LoanDrilldownRouterTabSwitcher";
import LoanGlobalSearch from "./components/LoanGlobalSearch";
import LoanDrilldownScreenSidebar from "./LoanDrilldownScreenSidebar";
import LoanSizerUploader from "./shared/LoanSizerUploader";
import LoanDrilldownAppraisalsTab from "./tabs/LoanDrilldownAppraisalsTab/LoanDrilldownAppraisalsTab";
import LoanDrilldownBorrowersTab from "./tabs/LoanDrilldownBorrowersTab/LoanDrilldownBorrowersTab";
import LoanDrilldownChecklistTab from "./tabs/LoanDrilldownChecklistTab/LoanDrilldownChecklistTab";
import LoanDrilldownCommunicationTab from "./tabs/LoanDrilldownCommunicationTab/LoanDrilldownCommunicationTab";
import LoanDrilldownConditionsTab from "./tabs/LoanDrilldownConditionsTab";
import LoanDrilldownContactsTab from "./tabs/LoanDrilldownContactsTab/LoanDrilldownContactsTab";
import LoanDrilldownDetailsTab from "./tabs/LoanDrilldownDetailsTab/LoanDrilldownDetailsTab";
import LoanDrilldownEntityTab from "./tabs/LoanDrilldownEntityTab/LoanDrilldownEntityTab";
import LoanDrilldownExceptionsTab from "./tabs/LoanDrilldownExceptionsTab/LoanDrilldownExceptionsTab";
import LoanDrilldownFeesTab from "./tabs/LoanDrilldownFeesTab/LoanDrilldownFeesTab";
import LoanDrilldownFilesTab from "./tabs/LoanDrilldownFilesTab/LoanDrilldownFilesTab";
import LoanDrilldownLetterOfIntentTab from "./tabs/LoanDrilldownLetterOfIntentTab";
import LoanDrilldownPropertiesTab from "./tabs/LoanDrilldownPropertiesTab/LoanDrilldownPropertiesTab";
import LoanDrilldownSizerTab from "./tabs/LoanDrilldownSizerTab";
import LoanDrilldownSummaryTab from "./tabs/LoanDrilldownSummaryTab";
import LoanDrilldownTermsSheetHhbTab from "./tabs/LoanDrilldownTermsSheetHhbTab";
import LoanDrilldownDocumentsTab from "./tabs/LoanDrilldownDocumentsTab/LoanDrilldownDocumentsTab";
import { useUserPreferences } from "../../hooks/utils/useUserPreferences";
import { useWorkspaceTabs } from "../../hooks/utils/useWorkspaceTabs";

// Register AG Grid modules
ModuleRegistry.registerModules([RichSelectModule]);
const initialLoanData = {
  _id: "67f44f69e685b472c479f7dd",
};

/** =====================
 * MAIN SCREEN COMPONENT
 * ===================== */
const LoanDrilldownScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: loanNumber } = useParams();
  const { getLoanById } = useUnderwritingHook();
  const { getUserPipeline } = usePipelineHook();

  const loanPipeline = useSelector((state) => state?.loanPipeline);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const [loanIdMap, setLoanIdMap] = useLocalStorageState("loanIdMap", {
    defaultValue: {},
  });

  const updateLoanIdMap = (loanNumber, loanId) => {
    setLoanIdMap((prevMap) => ({
      ...prevMap,
      [loanNumber]: loanId,
    }));
  };

  const fetchLoanById = (loanId) => {
    getLoanById({
      loanId,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        if (data.loanNumber && loanId) {
          updateLoanIdMap(data.loanNumber, loanId);
        }
      },
      onFailFn: () => {
        updateLoanIdMap(loanNumber, null);
        dispatch(loanDrilldownSet({}));
      },
      // onFailFn: () => dispatch(loanDrilldownSet({})),
    });
  };

  const findLoanInPipelineAndFetch = (pipeline) => {
    const foundLoan = pipeline.find(
      (l) => toString(l.loanNumber) === toString(loanNumber),
    );

    if (isString(foundLoan?._id)) {
      fetchLoanById(foundLoan._id);
    } else {
      dispatch(loanDrilldownSet({}));
    }
  };

  useEffect(() => {
    dispatch(navSidebarClose());
    if (loanNumber === "dev") {
      dispatch(loanDrilldownSet(initialLoanData));
      return;
    }
    if (!isNil(loanDrilldown?._id)) {
      return;
    }

    const storedLoanId = loanIdMap[loanNumber];

    if (isString(storedLoanId)) {
      fetchLoanById(storedLoanId);
    } else {
      if (isArray(loanPipeline) && loanPipeline.length > 0) {
        findLoanInPipelineAndFetch(loanPipeline);
      } else {
        getUserPipeline({
          onSuccessFn: (pipeline) => {
            dispatch(loanPipelineSet(pipeline));
            findLoanInPipelineAndFetch(pipeline);
          },
          onFailFn: () => dispatch(loanDrilldownSet({})),
        });
      }
    }
  }, [loanNumber]);

  if (isNil(loanDrilldown)) {
    return (
      <Flx center sx={{ p: 4, height: "100vh" }}>
        <CircularProgress />
      </Flx>
    );
  }

  if (isObject(loanDrilldown) && isEmpty(loanDrilldown)) {
    return (
      <Flx gap={1} column center sx={{ p: 4, height: "100vh" }}>
        <SearchOffOutlined fontSize="large" />
        <Htag h1>No Loan Found</Htag>
        <Txt>We encountered a problem trying to retrieve your loan.</Txt>
        <Flx sx={{ mt: 1 }}>
          <Button
            startIcon={<KeyboardReturnOutlined />}
            onClick={() => navigate("/loans")}
          >
            Return to Pipeline
          </Button>
        </Flx>
      </Flx>
    );
  }

  return <LoanDrilldownContent navigate={navigate} />;
};

/** =========================
 * CONTENT RENDERER
 * ========================= */
const LoanDrilldownContent = memo(({ navigate }) => {
  const [showTotals, setShowTotals] = useState(false);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const showChecklist = useSelector((state) => state.sidebar?.checklist);
  const { type } = useSelector((state) => state.sidebar);
  const baseUrl = useMemo(
    () => `/loan/${loanDrilldown?.loanNumber}`,
    [loanDrilldown],
  );
  const { pinned } = showChecklist;
  const dispatch = useDispatch();

  const toggleShowTotals = useCallback(() => {
    setShowTotals((s) => !s);
  }, []);

  const closeTotals = useCallback(() => {
    setShowTotals(false);
  }, []);
  return (
    <Flx
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        // background: "red",
        // background: "#f8fafc",
        // background: "#f8fafc",
        // background: "rgb(250, 250, 248)",
        // background: "#f8f7f5",
        background: "#f1f5f9",
        overflow: "hidden",
        // background: "#f5f8fa",
      }}
    >
      {/* <LoanDrilldownTotalsLeftSidebar
        show={showTotals}
        onClose={closeTotals}
        onOpen={toggleShowTotals}
      /> */}
      <Box
        id="overflow-root"
        sx={{
          overflowY: "auto",
          flexGrow: 1,
          height: "100%",
          // "&::-webkit-scrollbar-track": { backgroundColor: "#f8fafc" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f5f8fa" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
          },
          scrollbarColor: "#bbb #f5f8fa",
        }}
      >
        <DrilldownHeader navigate={navigate} />

        <ScreenContent
          padding={2}
          sx={{
            overflowX: "auto",
            background: "#ffffff",
          }}
        >
          <OverviewDisplayFields />
        </ScreenContent>
        <NavigationBar baseUrl={baseUrl} />
        <ContentRouter baseUrl={baseUrl} />
      </Box>
      {pinned && <LoanChecklist />}
      {/* <LoanDrilldownScreenSidebar /> */}
      <LoanDrilldownScreenSidebar />
      <FilePreviewSideCardModal loan={loanDrilldown} />
      <LoanSizerUploader
        show={type === loanDrilldownSidebarTypes.SIZER_UPLOAD}
        onClose={() => dispatch(sidebarClear())}
      />
    </Flx>
  );
});

/** =========================
 * DRILLDOWN HEADER
 * ========================= */
const DrilldownHeader = memo(({ navigate }) => {
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();
  // const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanId = useMemo(() => loanDrilldown?.loanNumber, [loanDrilldown]);
  const handleReturnToPipeline = () => {
    // dispatch(loanDrilldownClear());
    // dispatch(sidebarReset());
    clearLoanDrilldownData();
    navigate("/loans");
  };

  const loanTitle = useMemo(() => {
    if (isNil(loanDrilldown?.loanName)) {
      return `Loan #${loanDrilldown?.loanNumber}`;
    }
    return loanDrilldown?.loanName || `Loan #${loanDrilldown?.loanNumber}`;
  }, [loanDrilldown]);

  return (
    <ScreenHeader
      suppressBottomPadding
      titleTopContent={
        <Flx ac jb gap={1} sx={{ mb: 1.2 }} wrap>
          <Flx ac gap={1} wrap>
            <LoanSearchButton />
            <Link
              onClick={handleReturnToPipeline}
              sx={{
                fontSize: "12px",
                cursor: "pointer",
                fontFamily: "var(--ibm)",
              }}
            >
              Loan Pipeline
            </Link>
            <ChevronRight sx={{ fontSize: "12px" }} />
            <Link
              sx={{
                fontSize: "12px",
                cursor: "pointer",
                fontFamily: "var(--ibm)",
              }}
            >
              Loan {loanId}
            </Link>

            <LoanTagsRenderer sx={{ ml: 1.5 }} />
          </Flx>

          <Flx g={1} wrap ac>
            <LoanGlobalSearch sx={{ mr: 1 }} />
            <SendLoanApplicationsDropdownButton />
            <LoanSyncDropdownButton />

            <UploadToLoanDropdownButton />
            {/* <AddToLoanDropdownButton /> */}
          </Flx>
        </Flx>
      }
      title={loanTitle}
      titleEndContent={<EditLoanButtonWithModals />}
    />
  );
});

const LoanSearchButton = ({ children }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <Tooltip title="Search Loans">
        <IconButton
          sx={{ mr: -0.6, ml: -0.6 }}
          size="small"
          onClick={() => setShow(true)}
        >
          <SearchOutlined sx={{ fontSize: "13px" }} className="thin" />
        </IconButton>
      </Tooltip>
      <LoanSearchModal show={show} onClose={() => setShow(false)} />
    </>
  );
};

/** =========================
 * NAVIGATION TABS
 * ========================= */
const NavigationBar = memo(({ baseUrl }) => {
  const { isUnderwriter, isDevUser } = usePermissionsHook();
  const { isResfin, isLendmarq, isResilender } = useEnv();
  const user = useSelector((state) => state.user);

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        label: "Summary",
        path: "summary",
        icon: <DescriptionOutlined className="thin" />,
      },
      {
        label: "Loan Info",
        path: "loan-information",
        icon: <InfoOutlined className="thin" />,
      },
      {
        label: "Files",
        path: "files",
        icon: <FolderOutlined className="thin" />,
      },
      {
        label: "Entity",
        path: "entity",
        icon: <BusinessOutlined className="thin" />,
      },
      {
        label: "Borrowers",
        path: "borrowers",
        icon: <PeopleOutlined className="thin" />,
      },
      {
        label: "Properties",
        path: "properties",
        icon: <HomeWorkOutlined className="thin" />,
      },
      {
        label: "Contacts",
        path: "contacts",
        icon: <ContactsOutlined className="thin" />,
      },
    ];

    if ((isUnderwriter || isResfin || isLendmarq) && !isResilender) {
      baseTabs.push({
        label: "Communication",
        path: "communication",
        icon: <MailOutlined className="thin" />,
      });
    }

    baseTabs.push(
      {
        label: "Conditions",
        path: "conditions",
        icon: <RuleOutlined className="thin" />,
      },
      {
        label: "Exceptions",
        path: "exceptions",
        icon: <ReportProblemOutlined className="thin" />,
      },
      {
        label: "Sizer",
        path: "sizer",
        icon: <CalculateOutlined className="thin" />,
      },
      {
        label: "Appraisals",
        path: "appraisals",
        icon: <GavelOutlined className="thin" />,
      },
    );

    if ((isUnderwriter || isResfin || isLendmarq) && !isResilender) {
      baseTabs.push(
        {
          label: "Letter of Intent",
          path: "letter-of-intent",
          icon: <DescriptionOutlined className="thin" />,
        },

        {
          label: "Fees & Closing",
          path: "fees-and-closing",
          icon: <PaidOutlined className="thin" />,
        },
        {
          label: "Checklist",
          path: "checklist",
          icon: <ChecklistOutlined className="thin" />,
        },
      );
    }

    // baseTabs.push({
    //   label: "Checklist",
    //   path: "checklist",
    //   icon: <ChecklistOutlined className="thin" />,
    // });

    return baseTabs;
  }, [isUnderwriter, isResfin, isLendmarq, isResilender]);
  // const tabs = useMemo(() => {
  //   let baseTabs = [
  //     "Summary",
  //     "Loan Information",
  //     "Files",
  //     "Entity",
  //     "Borrowers",
  //     "Properties",
  //     "Conditions",
  //     "Exceptions",
  //     "Contacts",
  //     "Sizer",
  //     "Appraisals",
  //   ];

  //   // baseTabs = [...baseTabs, "Sizer", "Appraisals", "Loan Exceptions"];
  //   if ((isUnderwriter || isResfin || isLendmarq) && !isResilender) {
  //     baseTabs.push("Letter of Intent");
  //     baseTabs.push("Communication");
  //     baseTabs.push("Fees & Closing");
  //   }
  //   baseTabs.push("Checklist");
  //   return baseTabs;
  // }, [isUnderwriter, isResfin, isLendmarq, isResilender]);

  return (
    <Flx
      sx={{
        pl: 1,
        background: "#ffffff",
        borderTop: `1px solid #e2e8f0`,
        borderBottom: `1px solid #e2e8f0`,
        overflowY: "auto",
      }}
    >
      <LoanDrilldownRouterTabSwitcher
        tabs={tabs}
        iconSize={13}
        baseUrl={baseUrl}
        variant="background"
      />
    </Flx>
  );
});

/** =========================
 * DRILLDOWN CONTENT
 * ========================= */
const ContentRouter = memo(({ baseUrl }) => {
  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Routes>
        <Route path="/" element={<LoanDrilldownSummaryTab />} />
        <Route path="/summary" element={<LoanDrilldownSummaryTab />} />
        <Route path="/loan-information" element={<LoanDrilldownDetailsTab />} />
        <Route path="/files/*" element={<LoanDrilldownFilesTab />} />
        <Route path="/sizer" element={<LoanDrilldownSizerTab />} />
        <Route path="/contacts" element={<LoanDrilldownContactsTab />} />
        {/* <Route
          path="/communication/*"
          element={<LoanDrilldownCommunicationTab />}
        /> */}
        {/* <Route
          path="/communication/:communicationType?"
          element={<LoanDrilldownCommunicationTab />}
        /> */}

        <Route path="/conditions" element={<LoanDrilldownConditionsTab />} />
        <Route path="/documents" element={<LoanDrilldownDocumentsTab />} />
        <Route
          path="/communication"
          element={<LoanDrilldownCommunicationTab />}
        />
        <Route
          path="/communication/:communicationType"
          element={<LoanDrilldownCommunicationTab />}
        />
        <Route
          path="/contacts/:contactId?"
          element={<LoanDrilldownContactsTab />}
        />

        <Route path="/borrowers" element={<LoanDrilldownBorrowersTab />} />
        <Route
          path="/borrowers/:borrowerId?"
          element={<LoanDrilldownBorrowersTab />}
        />

        <Route
          path="/properties/:propertyId?"
          element={<LoanDrilldownPropertiesTab />}
        />
        <Route path="/appraisals" element={<LoanDrilldownAppraisalsTab />} />
        <Route path="/entity" element={<LoanDrilldownEntityTab />} />
        <Route path="/fees-and-closing/*" element={<LoanDrilldownFeesTab />} />
        <Route
          path="/letter-of-intent"
          element={<LoanDrilldownLetterOfIntentTab />}
        />
        <Route
          path="/terms-sheet"
          element={<LoanDrilldownTermsSheetHhbTab />}
        />
        <Route path="/exceptions" element={<LoanDrilldownExceptionsTab />} />
        <Route
          path="/loan-exceptions"
          element={<LoanDrilldownExceptionsTab />}
        />
        <Route path="/checklist" element={<LoanDrilldownChecklistTab />} />
        <Route path="*" element={<Navigate to={`${baseUrl}`} replace />} />
      </Routes>
    </Box>
  );
});

const EditLoanButtonWithModals = memo(() => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { clearLoanDrilldownData } = useReduxLoanDrilldownHook();
  const [modal, setModal] = useState(null);

  const dispatch = useDispatch();
  const { getLoanById } = useUnderwritingHook();

  const onSuccessUpdate = useCallback(async (data) => {
    dispatch(loanDrilldownSet(data));
    dispatch(loanPipelineClear());
  }, []);

  const handleRefresh = () => {
    clearLoanDrilldownData();
    // dispatch(loanDrilldownClear());
    getLoanById({
      loanId: loanDrilldown?._id,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        // dispatch(sidebarReset());
      },
      onFailFn: () => dispatch(loanDrilldownSet({})),
    });
  };

  // const options = useMemo(
  //   () => [
  //     {
  //       label: "Edit Loan Name",
  //       onClick: () => setModal("name"),
  //       icon: <DriveFileRenameOutline />,
  //     },
  //     {
  //       label: "Edit Loan Tags",
  //       onClick: () => setModal("tags"),
  //       icon: <LocalOfferOutlined />,
  //     },
  //   ],
  //   []
  // );

  return (
    <Flx ac wrap>
      {/* <Tooltip title="Rename Loan Name">
        <Box>
          <DropdownButton
            size="small"
            icon={<EditOutlined fontSize="small" />}
            options={options}
          />
        </Box>
      </Tooltip> */}
      <Tooltip title="Edit Loan Name">
        <IconButton size="small" onClick={() => setModal("name")}>
          <EditOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit Loan Tags">
        <IconButton size="small" onClick={() => setModal("tags")}>
          <LocalOfferOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <FavoriteLoanButton />
      <PinLoanTabButton />
      {/* <PinLoanButton /> */}
      <Tooltip title="Refresh loan data">
        <IconButton size="small" onClick={handleRefresh}>
          <RefreshOutlined fontSize="small" />
        </IconButton>
      </Tooltip>

      <UpdateLoanDataModal
        loan={modal === "name" ? loanDrilldown : null}
        field={"loanName"}
        inputLabel={"New Loan Name"}
        type="text"
        onClose={() => setModal(null)}
        onSuccessFn={() => handleRefresh()}
      />
      <LoanTagsModal
        show={modal === "tags"}
        onClose={() => setModal(null)}
        onSuccessFn={onSuccessUpdate}
      />
    </Flx>
  );
});

const FAVORITES_STORAGE_KEY = "lo-pipeline-favorite-loans";

const getFavoriteLoanIds = () => {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

const persistFavoriteLoanIds = (ids) =>
  localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(ids));

const FavoriteLoanButton = () => {
  const { syncPreferences } = useUserPreferences();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const [favoriteIds, setFavoriteIds] = useState(() => getFavoriteLoanIds());

  const loanId = loanDrilldown?._id;
  const isFavorite = loanId ? favoriteIds.includes(loanId) : false;

  const handleToggleFavorite = useCallback(() => {
    if (!loanId) return;
    setFavoriteIds((prev) => {
      const updated = prev.includes(loanId)
        ? prev.filter((id) => id !== loanId)
        : [...prev, loanId];
      persistFavoriteLoanIds(updated);
      return updated;
    });
    syncPreferences();
  }, [loanId, syncPreferences]);

  return (
    <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
      <IconButton size="small" onClick={handleToggleFavorite}>
        {isFavorite ? (
          <StarRounded sx={{ fontSize: 20, color: "#f59e0b" }} />
        ) : (
          <StarOutlineRounded sx={{ fontSize: 20 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

const PinLoanTabButton = () => {
  const { toggleTab, isTabPinned, loading } = useWorkspaceTabs();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const loanId = loanDrilldown?._id;
  const loanNumber = loanDrilldown?.loanNumber;
  const loanName = loanDrilldown?.loanName;
  const pinned = loanNumber ? isTabPinned(loanNumber) : false;

  const handleToggle = useCallback(() => {
    if (!loanId || !loanNumber) return;
    toggleTab({ _id: loanId, loanNumber, loanName });
  }, [loanId, loanNumber, loanName, toggleTab]);

  return (
    <Tooltip title={pinned ? "Unpin from tabs" : "Pin to tabs"}>
      <IconButton size="small" onClick={handleToggle} disabled={loading}>
        {loading ? (
          <CircularProgress size={18} sx={{ color: "#9ca3af" }} />
        ) : pinned ? (
          <PushPin sx={{ fontSize: 20, color: "#2962ff" }} />
        ) : (
          <PushPinOutlined sx={{ fontSize: 20 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

/** =========================
 * HELPER COMPONENTS
 * ========================= */

const OverviewDisplayFields = memo(() => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { isLaptopSized } = useMediaQueryHook();

  const borrowerNames = useMemo(() => {
    const borrowers = loanDrilldown?.borrowers;
    if (isEmpty(borrowers)) {
      return "None";
    }

    return borrowers
      ?.map((b) => {
        const { firstName, lastName } = b;
        if (isNil(firstName) && isNil(lastName)) {
          return "Unknown";
        }
        return `${firstName} ${lastName}`;
      })
      .join(", ");
  }, [loanDrilldown?.borrowers]);

  const propertyNames = useMemo(() => {
    const properties = loanDrilldown?.subjectProperties;
    if (isEmpty(properties)) {
      return "None";
    }

    return properties
      ?.map((p) => {
        return `${isNil(p?.address?.fullAddress) ? "N/A" : p?.address?.fullAddress}`;
      })
      .join(", ");
  }, [loanDrilldown?.subjectProperties]);

  return (
    <RffLoanDataGroup
      sx={{
        py: 1,
        px: 0.4,
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        // mt: 1,
        ".input-wrapper-root , .MuiDivider-root": {
          mr: 2,
        },
        ".MuiGrid-root": {
          // mr: 2,
          flexShrink: 0,
          // minWidth: "72px",
          ".MuiFormLabel-root , .MuiTypography-root": {
            whiteSpace: "nowrap",
          },
        },
      }}
      row
    >
      <RffDisplayField
        name="loanNumber"
        label="Loan #"
        type="select"
        data={loanDrilldown}
      />
      <RffDisplayField
        name={"loanStatus"}
        label={"Status"}
        type={"select"}
        data={loanDrilldown}
      />
      {loanDrilldown?.subStatus && (
        <RffDisplayField
          name={"subStatus"}
          label={"Sub-Status"}
          type={"select"}
          data={loanDrilldown}
        />
      )}
      {loanDrilldown?.sync_loan_id && (
        <RffDisplayField
          name={"sync_loan_id"}
          label={"Loan Sync ID"}
          // label={"External Loan Sync ID"}
          data={loanDrilldown}
        />
      )}

      {loanDrilldown?.last_integration_sync && (
        <RffDisplayField
          name={"last_integration_sync"}
          label={"Last Synced"}
          data={loanDrilldown}
          type={"date"}
        />
      )}

      <Seperator />
      <RffDisplayField
        name="loanProductType"
        // label="Product"
        label="Loan Product"
        type="select"
        data={loanDrilldown}
      />
      <RffDisplayField
        name="loanPurpose"
        // label="Purpose"
        label="Loan Purpose"
        type="select"
        data={loanDrilldown}
      />
      <RffDisplayField
        name="totalLoanAmount"
        label="Total Loan Amount"
        type="dollar"
        data={loanDrilldown}
      />
      <Seperator />
      <RffDisplayField
        label="Entity Name"
        startContent={<AddAlertOutlined />}
        name="borrowerEntity.entityName"
        emptyValue="Not Set"
        maxWidth={isLaptopSized ? "140px" : null}
        color={
          isNil(loanDrilldown?.borrowerEntity?.entityName) ? red[500] : null
        }
        icon={
          isNil(loanDrilldown?.borrowerEntity?.entityName) ? (
            <ErrorOutline className="thin" />
          ) : null
        }
      />
      <RffDisplayField
        label={
          size(loanDrilldown?.borrowers) > 1
            ? `Borrowers (${size(loanDrilldown?.borrowers)})`
            : "Borrower"
        }
        value={borrowerNames}
        maxWidth={isLaptopSized ? "140px" : null}
      />
      <RffDisplayField
        label={
          size(loanDrilldown?.subjectProperties) > 1
            ? `Properties (${size(loanDrilldown?.subjectProperties)})`
            : "Property"
        }
        value={propertyNames}
        maxWidth={isLaptopSized ? "140px" : null}
      />
      <Seperator />
      <LoanPermittedUsersAvatarStack />
    </RffLoanDataGroup>
  );
});

// const LoanPermittedUsersAvatarStack = ({ children }) => {
//   const loanDrilldown = useSelector((state) => state.loanDrilldown);
//   const { updateLoanPermittedUsers, loading } = useUnderwritingHook();
//   const dispatch = useDispatch();
//   const users = useSelector((state) => state.users);
//   const [show, setShow] = useState(false);

//   const permittedUsersNames = useMemo(() => {
//     const permittedUsers = loanDrilldown?.permittedUsers;
//     if (isEmpty(permittedUsers)) {
//       return "None";
//     }

//     const permittedUsersFullnames = permittedUsers.map((user) => {
//       return getUserFullNameFromEmail(users, user);
//     });

//     return permittedUsersFullnames.join(", ");
//   }, [loanDrilldown?.permittedUsers, users]);

//   const permittedUsersFullNames = useMemo(() => {
//     const permittedUsers = loanDrilldown?.permittedUsers;
//     if (isEmpty(permittedUsers)) {
//       return [];
//     }
//     return permittedUsers.map((user) => {
//       return getUserFullNameFromEmail(users, user);
//     });
//   }, [loanDrilldown?.permittedUsers, users]);

//   const initialValues = useMemo(() => {
//     return {
//       permittedUsers: loanDrilldown?.permittedUsers || [],
//     };
//   }, [loanDrilldown?.permittedUsers]);

//   const onSubmit = (values) => {
//     const updatedPermittedUsers = values?.permittedUsers;
//     console.log("userEmail", updatedPermittedUsers);
//     updateLoanPermittedUsers({
//       loanId: loanDrilldown?._id,
//       permittedUsers: updatedPermittedUsers,
//       onSuccessFn: (res) => {
//         dispatch(loanDrilldownSet(res));
//         setShow(false);
//       },
//     });
//   };

//   return (
//     <>
//       {/* <Box sx={{ position: "relative" }}>
//         <Tooltip title="Add permitted users to view this loan">
//           <IconButton
//             sx={{
//               position: "absolute",
//               left: "115px",
//               top: "-4px",
//               zIndex: 1,
//               color: "text.secondary",
//             }}
//             size="small"
//             onClick={() => setShow(true)}
//           >
//             <AddRounded fontSize="small" />
//           </IconButton>
//         </Tooltip>

//         <RffDisplayField
//           label={`Permitted Users (${size(loanDrilldown?.permittedUsers)})`}
//           value={permittedUsersNames}
//           maxWidth="300px"
//         />
//       </Box> */}
//       <AvatarStackWithAction
//         label="Permitted Users"
//         onAddClick={() => setShow(true)}
//         onAddTooltip="Add permitted users to view this loan"
//         items={permittedUsersFullNames}
//       />

//       <BasicModal
//         show={show}
//         onClose={() => setShow(false)}
//         title="Permitted Users"
//         maxWidth="sm"
//       >
//         <Flx column gap={1} sx={{ mt: 1, mb: 3 }}>
//           <RffDisplayField
//             label={`Current Permitted Users (${size(loanDrilldown?.permittedUsers)})`}
//             value={permittedUsersNames}
//           />
//         </Flx>
//         <Divider sx={{ mb: 3 }} />
//         <RffForm
//           loading={loading}
//           onSubmit={onSubmit}
//           submitText="Add User"
//           initialValues={initialValues}
//         >
//           <RffGroup>
//             <RffSelectMultipleUserEmailField
//               name="permittedUsers"
//               displayFullLabel
//               label="Select user to add to loan"
//             />
//           </RffGroup>
//         </RffForm>
//       </BasicModal>
//     </>
//   );
// };

const Seperator = ({ children }) => {
  return <Divider orientation="vertical" flexItem sx={{ mx: 0 }} />;
};

export default LoanDrilldownScreen;
